import _ from 'lodash';
import { Common } from './common';
import { Pagination } from './models/common';
import {
  CreateValues,
  Encounter,
  EncounterView,
  EncounterWithPatient,
  FindAllOptions,
  FindAllViewOptions,
  IntakeForm,
  Timeframe,
  UpdateValues,
  ValidationKind,
} from './models/encounter';
import { FamilyMedicalHistory } from './models/familyMedicalHistory';
import { Fax } from './models/fax';
import { AggregatedValidations } from './models/note';
import { GuarantorToExport } from './models/patient/guarantor';
import {
  InsuranceInformationUpdates,
  TerminatedInsurance,
} from './models/patient/insuranceInformation';
import { VerifyRegistrationSections } from './models/practice/preference';
import { OutputKind } from './models/scenario';
import { ScheduledAppointment } from './models';

export class API {
  common: Common;

  constructor(common: Common) {
    this.common = common;
  }

  async findAll(options?: FindAllOptions): Promise<EncounterWithPatient[]> {
    const url = this.common.constructPath('/encounters', options);
    const { encounters } = await this.common.getJSON(url);
    return encounters;
  }

  async findAllView(
    options?: FindAllViewOptions,
  ): Promise<Pagination<EncounterView>> {
    const url = this.common.constructPath('/encounters/view', options);
    return this.common.getJSON(url);
  }

  async findOneById(encounterId: string): Promise<EncounterWithPatient> {
    const url = this.common.constructPath(`/encounters/${encounterId}`);
    const { encounter } = await this.common.getJSON(url);
    return encounter;
  }

  async findAppointmentNote(encounterId: string): Promise<string> {
    const url = this.common.constructPath(
      `/encounters/${encounterId}/appointment-note`,
    );
    const { appointmentNote } = await this.common.getJSON(url);
    return appointmentNote;
  }

  async findClinicalComment(encounterId: string): Promise<string> {
    const url = this.common.constructPath(
      `/encounters/${encounterId}/clinical-comment`,
    );
    const { clinicalComment } = await this.common.getJSON(url);
    return clinicalComment;
  }

  async findGeneralComment(encounterId: string): Promise<string> {
    const url = this.common.constructPath(
      `/encounters/${encounterId}/general-comment`,
    );
    const { generalComment } = await this.common.getJSON(url);
    return generalComment;
  }

  async findGuarantor(encounterId: string): Promise<GuarantorToExport> {
    const url = this.common.constructPath(
      `/encounters/${encounterId}/guarantors`,
    );
    return this.common.getJSON(url);
  }

  async findFamilyMedicalHistory(
    encounterId: string,
  ): Promise<FamilyMedicalHistory> {
    const url = this.common.constructPath(
      `/encounters/${encounterId}/family-medical-history`,
    );
    return this.common.getJSON(url);
  }

  async findScheduledAppointment(
    encounterId: string,
  ): Promise<ScheduledAppointment.ScheduledAppointment> {
    const url = this.common.constructPath(
      `/encounters/${encounterId}/scheduled-appointment`,
    );
    const { scheduledAppointment } = await this.common.getJSON(url);
    return scheduledAppointment;
  }

  async findTerminatedInsurances(
    encounterId: string,
  ): Promise<TerminatedInsurance[]> {
    const url = this.common.constructPath(
      `/encounters/${encounterId}/insurance-information/terminated`,
    );
    const { insurances } = await this.common.getJSON(url);
    return insurances;
  }

  async findAllFaxes(id: string): Promise<Fax[]> {
    const url = this.common.constructPath(`/encounters/${id}/faxes`);
    return this.common.getJSON(url);
  }

  async findAllIntakeForms(encounterId: string): Promise<IntakeForm[]> {
    const url = this.common.constructPath(`/encounters/${encounterId}/forms`);
    return this.common.getJSON(url);
  }

  async findVerifyRegistrationSections(
    encounterId: string,
  ): Promise<{ verifyRegistrationSections: VerifyRegistrationSections[] }> {
    const url = this.common.constructPath(
      `/encounters/${encounterId}/verify-registration`,
    );
    return this.common.getJSON(url);
  }

  async getPatientInsuranceInformationUpdates(
    encounterId: string,
  ): Promise<InsuranceInformationUpdates> {
    const url = this.common.constructPath(
      `/encounters/${encounterId}/insurance-information`,
    );
    const { patientInsuranceInformationUpdates } =
      await this.common.getJSON(url);
    return patientInsuranceInformationUpdates;
  }

  async create(encounter: CreateValues): Promise<Encounter> {
    const url = this.common.constructPath('/encounters');
    return this.common.postJSON(url, encounter);
  }

  async checkIn(encounterId: string): Promise<boolean> {
    const url = this.common.constructPath(
      `/encounters/${encounterId}/checkedIn`,
    );
    const { success } = await this.common.putJSON(url);
    return success;
  }

  async runFingerprint(
    encounterId: string,
    body: { kinds: OutputKind[]; queryOnly?: boolean },
  ): Promise<{ success: boolean }> {
    const url = this.common.constructPath(
      `/encounters/${encounterId}/fingerprint`,
    );
    return this.common.postJSON(url, body);
  }

  async start(encounterId: string, startedTimestamp: Date): Promise<boolean> {
    const url = this.common.constructPath(`/encounters/${encounterId}/started`);
    const { success } = await this.common.putJSON(url, {
      startedTimestamp,
    });
    return success;
  }

  async end(
    encounterId: string,
    endEncounterPressedTimestamp: Date,
  ): Promise<boolean> {
    const url = this.common.constructPath(`/encounters/${encounterId}/ended`);
    const { success } = await this.common.putJSON(url, {
      endEncounterPressedTimestamp,
    });
    return success;
  }

  async complete(encounterId: string): Promise<boolean> {
    const url = this.common.constructPath(
      `/encounters/${encounterId}/completed`,
    );
    const { success } = await this.common.putJSON(url);
    return success;
  }

  async update(encounterId: string, values: UpdateValues): Promise<Encounter> {
    const url = this.common.constructPath(`/encounters/${encounterId}`);
    return this.common.putJSON(url, values);
  }

  async getValidation(
    encounterId: string,
    applicableValidations: ValidationKind[] = [],
  ): Promise<{ validation: AggregatedValidations }> {
    const url = this.common.constructPath(
      `/encounters/${encounterId}/validation`,
      { applicableValidations: applicableValidations.join(',') },
    );
    return this.common.getJSON(url);
  }

  async remove(encounterId: string): Promise<boolean> {
    const url = this.common.constructPath(`/encounters/${encounterId}`);
    const { success } = await this.common.deleteJSON(url);
    return success;
  }

  async copy(encounterId: string): Promise<boolean> {
    const url = this.common.constructPath(`/encounters/${encounterId}/copy`);
    const { success } = await this.common.putJSON(url);
    return success;
  }

  async fetchVocabulary(
    encounterId: string,
  ): Promise<{ vocabulary: string[]; profanity: string[] }> {
    const url = this.common.constructPath(
      `/encounters/${encounterId}/vocabulary`,
    );
    return this.common.getJSON(url);
  }

  async findAllForEhrSystemByIds(query: {
    encounterId: string;
    activeOnly: boolean;
    isFuture: boolean;
    timeframe: Timeframe;
  }): Promise<EncounterWithPatient[]> {
    const url = this.common.constructPath(
      `/encounters/${query.encounterId}/relative`,
      _.omit(query, 'encounterId'),
    );
    const { encounters } = await this.common.getJSON(url);
    return encounters;
  }
}
